import {
  Badge,
  Box,
  Button,
  Group,
  Modal,
  Text,
  Textarea,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { hasRole, useCurrentUser } from "../hooks/useAuth.js";
import {
  useDeleteNote,
  useUpdateNote,
  type CustomerNote,
} from "../hooks/useCustomers.js";
import { ApiErrorAlert } from "./ApiErrorAlert.js";

/** Deutsches Datumsformat — dieselbe Darstellung wie im Kundendetail. */
function formatDate(iso: string | null | undefined): string {
  if (!iso) return "–";
  return new Date(iso).toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

/**
 * Eine Kundennotiz mit Ändern und Löschen.
 *
 * Die Knöpfe erscheinen nur beim Verfasser und bei Admins — die verbindliche
 * Grenze zieht aber der Server (`NOTE_NOT_OWN`, 403). Hier geht es nur darum,
 * niemanden ins Leere klicken zu lassen.
 *
 * Eine geänderte Notiz wird als solche gekennzeichnet. Ohne diesen Hinweis
 * könnte sich der Text unter jemandem verändern, der sich auf das verlässt,
 * was er gelesen hat.
 */
export function CustomerNoteItem({
  note,
  customerId,
  segmentName,
}: {
  note: CustomerNote;
  customerId: string;
  segmentName: string;
}) {
  const { t } = useTranslation();
  const { user } = useCurrentUser();
  const [bearbeitet, setBearbeitet] = useState(false);
  const [entwurf, setEntwurf] = useState(note.note);
  const [loeschenOffen, loeschen] = useDisclosure(false);

  const update = useUpdateNote(customerId);
  const remove = useDeleteNote(customerId);

  const darfAendern =
    user != null &&
    (note.authorId === user.employeeId || hasRole(user, "admin"));

  return (
    <Box
      p="sm"
      style={(theme) => ({
        border: `1px solid ${theme.colors.gray[3]}`,
        borderRadius: theme.radius.sm,
      })}
    >
      <Group justify="space-between" mb={4}>
        <Text size="sm" fw={600}>
          {note.authorName ?? t("common.dashed")}
        </Text>
        <Group gap={6}>
          <Badge color="blue" variant="light" size="sm">
            {segmentName}
          </Badge>
          {note.crossSegment && (
            <Badge color="grape" variant="light" size="sm">
              {t("pages.customers.detail.noteCrossSegmentBadge")}
            </Badge>
          )}
          {note.updatedAt ? (
            <Badge color="gray" variant="light" size="sm">
              {t("pages.customers.detail.noteEdited", {
                date: formatDate(note.updatedAt),
              })}
            </Badge>
          ) : null}
          <Text size="xs" c="dimmed">
            {formatDate(note.createdAt)}
          </Text>
        </Group>
      </Group>

      {update.error ? <ApiErrorAlert error={update.error} /> : null}
      {remove.error ? <ApiErrorAlert error={remove.error} /> : null}

      {bearbeitet ? (
        <>
          <Textarea
            value={entwurf}
            onChange={(e) => setEntwurf(e.currentTarget.value)}
            minRows={3}
            autosize
            mb="xs"
          />
          <Group gap="xs">
            <Button
              size="compact-sm"
              loading={update.isPending}
              disabled={entwurf.trim().length === 0}
              onClick={() =>
                update.mutate(
                  { noteId: note.id, note: entwurf.trim() },
                  { onSuccess: () => setBearbeitet(false) },
                )
              }
            >
              {t("common.save")}
            </Button>
            <Button
              size="compact-sm"
              variant="subtle"
              onClick={() => {
                setEntwurf(note.note);
                setBearbeitet(false);
              }}
            >
              {t("common.cancel")}
            </Button>
          </Group>
        </>
      ) : (
        <>
          <Text size="sm">{note.note}</Text>
          {darfAendern ? (
            <Group gap="xs" mt="xs">
              <Button
                size="compact-xs"
                variant="subtle"
                onClick={() => setBearbeitet(true)}
              >
                {t("common.edit")}
              </Button>
              <Button
                size="compact-xs"
                variant="subtle"
                color="red"
                onClick={loeschen.open}
              >
                {t("common.delete")}
              </Button>
            </Group>
          ) : null}
        </>
      )}

      <Modal
        opened={loeschenOffen}
        onClose={loeschen.close}
        title={t("pages.customers.detail.noteDeleteTitle")}
        size="sm"
        centered
      >
        <Text size="sm" mb="md">
          {t("pages.customers.detail.noteDeleteBody")}
        </Text>
        <Group justify="flex-end" gap="xs">
          <Button variant="subtle" onClick={loeschen.close}>
            {t("common.cancel")}
          </Button>
          <Button
            color="red"
            loading={remove.isPending}
            onClick={() =>
              remove.mutate(note.id, { onSuccess: () => loeschen.close() })
            }
          >
            {t("pages.customers.detail.noteDeleteConfirm")}
          </Button>
        </Group>
      </Modal>
    </Box>
  );
}
